import { useMemo, useState } from 'react';

import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';

import { paths } from 'src/routes/paths';

import { useTranslate } from 'src/locales';
import { useGetContactByIdQuery } from 'src/services/contacts/contacts.service';

import { ContactDetailSection } from 'src/sections/contacts/components/detail-card';
import { useContactActions } from 'src/sections/contacts/hooks/use-contact-actions';
import ContactDocumentsView from 'src/sections/contacts/components/contact-documents';

import RecordActionBar from '../record-action-bar';
import RecordPanelHeader from '../record-panel-header';
import { RecordHeaderModel } from '../record-panel.types';
import { splitRecordActions } from '../record-panel.utils';

// ----------------------------------------------------------------------

enum TabValue {
  DETAIL = 'detail',
  DOCUMENTS = 'documents',
}

/** Shown as buttons; every other contact action goes under "More". */
const PRIMARY_ACTIONS = ['add-to-list', 'send-email', 'call', 'add-task'];

/**
 * Contact body for the record panel's quick view (JT1-1529). Fetches the contact and feeds the shared
 * header (name, company/role subtitle, email + phone) and action bar: Add to list, Send email, Call and
 * Add task as buttons, and the rest of the contact's actions under "More", the same list the profile
 * page offers (JT1-1799). The body shows the contact detail card and the documents. The centred
 * overlay (name click) renders the full ContactsDetailView instead.
 */
export default function ContactRecordView({ id, hideActions = false }: { id: string; hideActions?: boolean }) {
  const { t } = useTranslate();

  const { data: contact, isLoading } = useGetContactByIdQuery(id, { skip: !id });

  const [currentTab, setCurrentTab] = useState<TabValue>(TabValue.DETAIL);

  const { actions, modals } = useContactActions({ mode: 'record', ids: [id] });
  const { primary, more } = splitRecordActions(actions, PRIMARY_ACTIONS);

  const header: RecordHeaderModel = useMemo(() => ({
    name: contact ? `${contact.first_name ?? ''} ${contact.last_name ?? ''}`.trim() : t('common.loading', { defaultValue: 'Loading…' }),
    subtitle: [contact?.position_title, contact?.company_name].filter(Boolean).join(' · ') || undefined,
    meta: [],
    contact: { email: contact?.email, phone: contact?.phone },
    fullProfileHref: paths.dashboard.contacts.detail(id),
  }), [contact, t, id]);

  if (isLoading || !contact) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ flexGrow: 1, py: 8 }}>
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <>
      <RecordPanelHeader model={header} />
      {/* hideActions: the org-chart quick-peek renders read-only. Its action modals live at app-root
          z-indexes (mailer 1000, task drawer 1200) far below the peek, so from a peek that sits over
          a company overlay they'd render hidden/focus-trapped — actions stay in the full profile. */}
      {!hideActions && <RecordActionBar actions={primary} moreActions={more} />}

      <Tabs
        value={currentTab}
        onChange={(_e, tab) => setCurrentTab(tab)}
        sx={{ px: 2.5, mt: 1, boxShadow: (theme) => `inset 0 -2px 0 0 ${theme.palette.divider}` }}
      >
        {/* "Notes", not "Details": this tab holds only the notes thread (the full profile's Details
            tab adds the activity feed). */}
        <Tab value={TabValue.DETAIL} label={t('common.notes', { defaultValue: 'Notes' })} />
        <Tab value={TabValue.DOCUMENTS} label={t('contacts.detail.tabs.documents', { defaultValue: 'Documents' })} />
      </Tabs>

      {currentTab === TabValue.DETAIL && (
        <Stack sx={{ px: 3, py: 2 }}>
          <ContactDetailSection contactId={id} />
        </Stack>
      )}

      {currentTab === TabValue.DOCUMENTS && (
        <Stack sx={{ px: 3, py: 2 }}>
          <ContactDocumentsView contactId={id} />
        </Stack>
      )}

      {!hideActions && modals}
    </>
  );
}
